import React, {Dispatch, SetStateAction, useMemo} from 'react'
import {
  Badge,
  Button,
  ButtonGroup,
  DateTimeRangePicker,
  Icon,
  Text,
} from '@therms/atalaya'
import {useUrlState} from '@therms/react-modules'
import {MdManageSearch} from 'react-icons/md'
import {IoClose} from 'react-icons/io5'
import {ReportsSearchFiltersModal} from '@src/modules/Reports/router/screens/Index/components/ReportsSearchFiltersModal.tsx'

interface ReportsSearchFiltersProps {
  filtersState: {
    selectedActivityTypeIds?: string[] | undefined
    selectedDateTimeRange?: {
      start?: string | undefined
      end?: string | undefined
    }
    selectedEventRefIds?: string[] | undefined
    selectedLocationRefIds?: string[] | undefined
    selectedRecordRefIds?: string[] | undefined
    selectedReportTypeIds?: string[] | undefined
    selectedUserIds?: string[] | undefined
    selectedUserRefIds?: string[] | undefined
  }
  searchText: string
  setFiltersState: (
    val: Partial<{
      selectedActivityTypeIds: string[] | undefined
      selectedDateTimeRange?: {
        start?: string | undefined
        end?: string | undefined
      }
      selectedReportTypeIds: string[] | undefined
      selectedUserIds: string[] | undefined
      selectedEventRefIds: string[] | undefined
      selectedLocationRefIds: string[] | undefined
      selectedRecordRefIds: string[] | undefined
      selectedUserRefIds: string[] | undefined
    }>,
  ) => void
  setSearchText: Dispatch<SetStateAction<string>>
  showLocationFilter?: boolean
}

function ReportsSearchFilters({
  filtersState,
  searchText,
  setFiltersState,
  setSearchText,
  showLocationFilter,
}: ReportsSearchFiltersProps) {
  const [showAdvanceFilters, setAdvanceFilters] = useUrlState(
    'reportadvancefilters',
    false,
  )

  const filterCount = useMemo(
    () =>
      (filtersState.selectedActivityTypeIds?.length || 0) +
      (filtersState.selectedEventRefIds?.length || 0) +
      (filtersState.selectedLocationRefIds?.length || 0) +
      (filtersState.selectedRecordRefIds?.length || 0) +
      (filtersState.selectedReportTypeIds?.length || 0) +
      (filtersState.selectedUserIds?.length || 0) +
      (filtersState.selectedDateTimeRange?.start ? 1 : 0) +
      (filtersState.selectedUserRefIds?.length || 0),
    [filtersState],
  )

  return (
    <div className="gap-y-xxs flex flex-col items-start justify-center">
      <div className="xxl:grid-cols-4 gap-xxs grid grid-cols-3">
        <div className="col-span-1 md:col-span-1">
          <Text
            placeholder="Search..."
            onChangeValue={setSearchText}
            value={searchText}
            search
          />
        </div>

        <div className="col-span-3 lg:col-span-2 xl:col-span-2">
          <DateTimeRangePicker
            onChangeValue={(val) => {
              setFiltersState({
                selectedDateTimeRange: val,
              })
            }}
            onClearValue={() =>
              setFiltersState({
                selectedDateTimeRange: {end: undefined, start: undefined},
              })
            }
            value={filtersState.selectedDateTimeRange}
          />
        </div>

        <div className="xxl:row-start-1 xxl:col-start-4 col-span-2 col-start-2 row-start-1 xl:col-start-1 xl:row-start-2">
          <ButtonGroup>
            {filterCount ? (
              <Button
                onClick={() => {
                  setAdvanceFilters(!showAdvanceFilters)
                }}
                size="base"
                subtle
                variant="info"
              >
                <span className="pr-xxs">
                  <Badge value={filterCount} variant="info" /> Filters Applied
                </span>

                <Icon Icon={MdManageSearch} />
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setAdvanceFilters(!showAdvanceFilters)
                }}
                size="base"
                subtle
                variant="neutral"
              >
                <span className="pr-xxs">Filters</span>

                <Icon Icon={MdManageSearch} />
              </Button>
            )}

            {(!!filtersState.selectedReportTypeIds ||
              filtersState.selectedActivityTypeIds ||
              filtersState.selectedDateTimeRange?.end ||
              filtersState.selectedDateTimeRange?.start ||
              filtersState.selectedEventRefIds ||
              filtersState.selectedLocationRefIds ||
              filtersState.selectedRecordRefIds ||
              filtersState.selectedUserRefIds ||
              searchText ||
              !!filtersState.selectedUserIds) && (
              <div>
                <Button
                  onClick={() => {
                    setAdvanceFilters(false)
                    setSearchText('')
                    setFiltersState({
                      selectedActivityTypeIds: undefined,
                      selectedDateTimeRange: {end: undefined, start: undefined},
                      selectedEventRefIds: undefined,
                      selectedLocationRefIds: undefined,
                      selectedRecordRefIds: undefined,
                      selectedReportTypeIds: undefined,
                      selectedUserIds: undefined,
                      selectedUserRefIds: undefined,
                    })
                  }}
                  subtle
                >
                  <Icon color="critical" Icon={IoClose} /> Clear
                </Button>
              </div>
            )}
          </ButtonGroup>
        </div>
      </div>

      {showAdvanceFilters && (
        <ReportsSearchFiltersModal
          filtersState={filtersState}
          onClose={() => setAdvanceFilters(false)}
          setFiltersState={setFiltersState}
          showLocationFilter={showLocationFilter}
        />
      )}
    </div>
  )
}

export {ReportsSearchFilters}
